# Privacy Policy

Last Updated: July 28, 2023.

**1. Introduction**
Welcome to our privacy policy. We are Logseq, Inc., a Delaware corporation (“Logseq”, “we”, or “us”). We are responsible for this website and the Logseq applications.

Logseq respects your privacy and is committed to protecting your personal data. This privacy policy (“**_Privacy Policy_**”) governs our collection and use of data in connection with our website (the “**_Site_**”), our applications, our website, and any services we offer (collectively, the “**_Services_**”). Terms capitalized but not defined in this Privacy Policy shall have the meaning ascribed to them in our Terms of Use.

This Privacy Policy sets forth the nature, purpose, use and sharing of any information that identifies or can be used to identify you, the visitors to the Site, and users of our Services (“**_Personal Information_**”). This website is not intended for children, and we do not knowingly collect data relating to children. If you believe we have information relating to any individual under the age of 13, please notify us using the methods listed in this policy and we will remove such information as soon as possible.

**A. Who We Are**
- Full name of legal entity: Logseq, Inc.
- Name of Data Protection Manager: Zhiyuan Chen
- Email address: support@logseq.com
- Postal address: 228 Park Avenue South, PMB 76315, New York, NY 10003, United States

  Our website may include links to or integrations with third-party websites, plug-ins and applications. Clicking on those links, accessing such integrated services, or enabling those connections may allow third parties to collect or share data about you. We do not control these third-party websites and are not responsible for their privacy statements. When you leave our website, we encourage you to read the privacy policy of every website you visit.

**B. What We Collect**

We act as a data “controller,” meaning that we collect certain Personal Information from you as you engage the website, our services, or contact us. Data privacy and protection laws across the United States and the European Union (“**_Privacy Laws_**”) apply to how we collect that information and what we use it for. To help us comply with Privacy Laws, and because we care about being transparent with you, below is a chart detailing what sort of personal information we collect about you, how we collect it, and why. In some cases, we may collect different information from you based on the type of relationship we have with you, and in such cases the differences are noted.

<table>
  <tr>
   <td>
<strong>Type of Data</strong>
   </td>
   <td><strong>Examples of Data Collected</strong>
   </td>
   <td><strong>How Collected</strong>
   </td>
   <td><strong>Why Collected/Legal Basis for Collection</strong>
   </td>
  </tr>
  <tr>
   <td><strong>Identity Data</strong>
   </td>
   <td>Email address
<br />
Username
   </td>
   <td>Provided by user when account is created.
   </td>
   <td>Necessary to provide services.
<br />
Collected with consent of user
   </td>
  </tr>
  <tr>
   <td><strong>Contact Data</strong>
   </td>
   <td>Billing address
<br />
Email address
   </td>
   <td>Provided by user when account is created.
   </td>
   <td>Necessary to provide services.
   </td>
  </tr>
  <tr>
   <td><strong>Transaction Data</strong>
   </td>
   <td>Details of payments to and from you and other users, as well as details about products and services purchased by you.
   </td>
   <td>Collected at the time of transaction and/or during contract execution.
   </td>
   <td>Necessary to provide services.
<br />
Our legitimate business interests allow us to collect such non-intrusive data as your purchasing history so we can more effectively market our products.
   </td>
  </tr>
  <tr>
   <td><strong>Technical Data</strong>
   </td>
   <td>Device information
<br />
Operating System (OS)
<br />
Platform (Electron/Browser)
<br />
Logseq release version
   </td>
   <td>Collected automatically through the Logseq app.
   </td>
   <td>Our legitimate business interests allow us to collect this non-intrusive data. 
   </td>
  </tr>
</table>

**C. Contact details**
You have the right to make a complaint at any time to the supervisory authority for data protection issues in your jurisdiction. We would, however, appreciate the chance to deal with your concerns before you approach such authorities so please contact us in the first instance.

**D. Changes to the privacy policy and your duty to inform us of changes**
It is important that the personal data we hold about you is accurate and current. Please keep us informed if your personal data changes during your relationship with us.

**E. Third-party privacy practices**
This website may include links to third-party websites, plug-ins and applications. Clicking on those links or enabling those connections may allow third parties to collect or share data about you. We do not control these third-party websites and are not responsible for their privacy statements. When you leave our website, we encourage you to read the privacy policy of every website you visit.

**2. The data we collect about you**
**A. Types of data**
Personal data, or personal information, means any information about an individual from which that person can be identified. It does not include data where the identity has been removed (anonymous data). We have grouped these different kinds of personal data together as follows:

- **Identity Data** may include first name, last name, company name, job title, website URL and other demographic data that you provide to us.
- **Contact Data** includes billing address, email address and telephone numbers.
- **Financial Data** includes bank account and payment card details.
- **Transaction Data** includes details about payments to and from you and other details of products and services you have purchased from us.
- **Technical Data** includes internet protocol address, your login data, device/browser type and version, time zone setting, location and time, device/software plug-in types and versions, operating system and platform and other technology on the devices you use to access this website.
- **Profile Data **includes** **your username and password, purchases made by you, your preferences, feedback and survey responses.
- **Embedded Data** covers the personal data about you which may be added to the Services by a third party.
- **Usage Data** includes information about how you use our website, products and services.
- **Marketing and Communications Data** includes your preferences in receiving marketing from us and our third parties and your communication preferences.

We also collect, use and share **Aggregated Data** such as statistical or demographic data for any purpose. Aggregated Data may be derived from your personal data but is not considered personal data in law as this data does **not** directly or indirectly reveal your identity. For example, we may aggregate your Usage Data to calculate the percentage of users accessing a specific website feature. However, if we combine or connect Aggregated Data with your personal data so that it can directly or indirectly identify you, we treat the combined data as personal data which will be used in accordance with this privacy policy.

We do not collect any **Special Categories of Personal Data** about you (this includes details about your race or ethnicity, religious or philosophical beliefs, sex life, sexual orientation, political opinions, trade union membership, information about your health and genetic and biometric data). Nor do we collect any information about criminal convictions and offences.

**B. If you fail to provide personal data**
Where we need to collect personal data by law, or under the terms of a contract we have with you and you fail to provide that data when requested, we may not be able to perform the contract we have or are trying to enter into with you (for example, to provide you with goods or services). In this case, we may have to cancel a product or service you have with us, but we will notify you if this is the case at the time.

3. **How is your personal data collected?**
   We use different methods to collect data from and about you are including through:

**Direct interactions.** You may give us your Identity, Contact, and Financial Data by filling in forms or by corresponding with us by post, phone, email or otherwise. This includes personal data you provide when you: create an account on our website, use our services, request marketing to be sent to you, or give us feedback.

**Automated technologies or interactions.** As you interact with our products, we may automatically collect Technical Data and Usage Data about your equipment, actions and patterns. We may collect this personal data by using cookies, server logs and other similar technologies. We may also receive Technical Data about you if you visit other websites or use other products employing our cookies or technology.

**Third parties or publicly available sources.** We may receive personal data about you from various third parties and public sources as set out below:

- Technical Data from analytics providers, advertising networks, and search information providers.
- Contact, Financial and Transaction Data from providers of technical, payment, distribution and delivery services.
- Identity and Contact Data from data brokers or aggregators.
- Identity and Contact Data from publicly availably sources.
- Contact, Identity, and Embedded Data from other users of our Services.

**4. How we use your personal data**
We will only use your personal data when the law allows us to. Most commonly, we will use your personal data in the following circumstances:

- Where we need to perform the contract we are about to enter into or have entered into with you.
- Where it is necessary for our legitimate interests (or those of a third party) and your interests and fundamental rights do not override those interests.
- Where we need to comply with a legal or regulatory obligation.

Generally, we do not rely on consent as a legal basis for processing your personal data other than in relation to sending third party direct marketing communications to you via email or text message. You have the right to withdraw consent to marketing at any time by contacting us.

**A. Purposes for which we will use your personal data**
We have set out below, in table format, a description of all the ways we plan to use your personal data, and which of the legal bases we rely on to do so. We have also identified what our legitimate interests are where appropriate.

Note that we may process your personal data for more than one lawful ground depending on the specific purpose for which we are using your data. Please contact us if you need details about the specific legal ground we are relying on to process your personal data where more than one ground has been set out in the table below.

<table>
  <tr>
   <td>
<strong>Purpose/Activity</strong>
   </td>
   <td><strong>Type of data</strong>
   </td>
   <td><strong>Lawful basis for processing including basis of legitimate interest</strong>
   </td>
  </tr>
  <tr>
   <td>To register you as a new customer
   </td>
   <td>(a) Identity 
<br />
(b) Contact
   </td>
   <td>Performance of a contract with you.
   </td>
  </tr>
  <tr>
   <td>To process and deliver your order of services including:
<br />
(a) Manage payments, fees and charges
<br />
(b) Collect and recover money owed to us
   </td>
   <td>(a) Identity 
<br />
(b) Contact 
<br />
(c) Financial 
<br />
(d) Transaction 
<br />
(e) Marketing and Communications
   </td>
   <td>(a) Performance of a contract with you. 
<br />
(b) Necessary for our legitimate interests (to recover debts due to us).
   </td>
  </tr>
  <tr>
   <td>To manage our relationship with you which will include:
<br />
(a) Notifying you about changes to our terms or privacy policy
<br />
(b) Asking you for feedback
   </td>
   <td>(a) Identity 
<br />
(b) Contact 
<br />
(c) Profile 
<br />
(d) Marketing and Communications
   </td>
   <td>(a) Performance of a contract with you. 
<br />
(b) Necessary to comply with a legal obligation.
<br />
(c) Necessary for our legitimate interests (to keep our records updated and to study how customers use our products/services).
   </td>
  </tr>
  <tr>
   <td>To administer and protect our business and products (including troubleshooting, data analysis, testing, system maintenance, support, reporting and hosting of data)  
   </td>
   <td>(a) Identity
<br />
(b) Contact
<br />
(c) Technical
<br />
(d) Profile
   </td>
   <td>(a) Necessary for our legitimate interests (for running our business, provision of administration and IT services, network security, to prevent fraud and in the context of a business reorganization or group restructuring exercise).
<br />
(b) Necessary to comply with a legal obligation.
   </td>
  </tr>
  <tr>
   <td>To use data analytics to improve our website, products/services, marketing, customer relationships and experiences
   </td>
   <td>(a) Technical 
<br />
(b) Usage 
<br />
(c) Profile
   </td>
   <td>Necessary for our legitimate interests (to define types of customers for our products and services, to keep our website updated and relevant, to develop our business and to inform our marketing strategy).
   </td>
  </tr>
  <tr>
   <td>To make suggestions and recommendations to you about goods or services that may be of interest to you
   </td>
   <td>(a) Identity 
<br />
(b) Contact 
<br />
(c) Technical 
<br />
(d) Usage 
<br />
(e) Profile 
   </td>
   <td>Necessary for our legitimate interests (to develop our products/services and grow our business).
   </td>
  </tr>
</table>

**B. Promotional offers from us**
We may use your Identity, Contact, Technical, Usage and Profile Data to form a view on what we think you may want or need, or what may be of interest to you. This is how we decide which products, services and offers may be relevant for you (we call this marketing).

You will receive marketing communications from us if you have requested information from us or purchased goods or services from us and, in each case, you have not opted out of receiving that marketing.

**C. Third-party marketing**
We will not share your personal data with any third party for marketing purposes.

**D. Opting out**
You can ask us or third parties to stop sending you marketing messages at any time by logging into the website and checking or unchecking relevant boxes to adjust your marketing preferences or by following the opt-out links on any marketing message sent to you.

Where you opt out of receiving these marketing messages, this will not apply to personal data provided to us as a result of a product/service purchase or other transaction.

**E. Cookies**
In order to improve our services and provide more convenient, relevant experiences to our customers, we and our agents may use “cookies,” “web beacons,” and similar devices to track your activities. A cookie is a small amount of data that is transferred to your browser by a web server and can only be read by the server that gave it to you. It functions as your identification card and enables us to record your passwords and preferences. It cannot be executed as code or deliver viruses. A web beacon is a small transparent .gif image that is embedded in an HTML page or email used to track when the page or email has been viewed. Most browsers are initially set to accept cookies, and most services that include similar devices are typically initially activated to collect data. You can set your browser to notify you when you receive a cookie, giving you the chance to decide whether or not to accept it.

**F. Change of purpose**
We will only use your personal data for the purposes for which we collected it, unless we reasonably consider that we need to use it for another reason and that reason is compatible with the original purpose. If you wish to get an explanation as to how the processing for the new purpose is compatible with the original purpose, please contact us.

If we need to use your personal data for an unrelated purpose, we will notify you and explain the legal basis which allows us to do so.

Please note that we may process your personal data without your knowledge or consent, in compliance with the above rules, where this is required or permitted by law.

**5. Disclosures of your personal data**
We may have to share your personal data with the parties set out below for the purposes set out in the table in section 3 above.

- Internal Third Parties as set out in Section 13.
- External Third Parties as set out in Section 13.
- Third parties to whom we may choose to sell, transfer, or merge parts of our business or our assets. Alternatively, we may seek to acquire other businesses or merge with them. If a change happens to our business, then the new owners may use your personal data in the same way as set out in this privacy policy.

We require all third parties to respect the security of your personal data and to treat it in accordance with the law. We do not allow our third-party service providers to use your personal data for their own purposes and only permit them to process your personal data for specified purposes and in accordance with our instructions.

**6. Data Processing**
In submitting any third-party personal data within the context of the Services (“**_Third-Party Data_**”), the parties agree that you shall be the Data Controller and you appoint us as a Data Processor of such data, for the purpose of providing the Services. To the extent that we are a “Data Processor” for you in respect of such Third-Party Data, we will:

- Implement appropriate technical and organizational measures to safeguard the Third-Party Data against any unauthorized or unlawful access, loss, destruction, theft, use or disclosure. We implement basic level security in accordance with United States law. If higher levels of security are required, please contact us.
- Limit access to the Third-Party Data only to those employees who need to know it to enable the Processor to perform the Services, and shall take appropriate steps to ensure the reliability of those of our employees or subcontractors who have access to such Third-Party Data.
- Only process the personal data as specified by this Privacy Policy and in accordance with your instructions, and will not use the Third-Party Data for any purposes other than those related to the performance of the Services or pursuant to your written instructions.
- Upon the expiry or termination of this Privacy Policy, or upon your request, cease any and all use of the Third-Party Data and destroy or return it to you.
- Not disclose the Third-Party Data to any third-party without your prior written consent. Such consent is granted on, for example, your instruction to share a dashboard with third parties (public or within a restricted group).

We as Data Processor may provide access to a subcontractor processor to any such Third-Party Data if we reasonably consider such access and processing necessary to the performance of the Services. In the event of such access and before the access takes place, we will ensure that an agreement with the third-party is in place which is sufficient to require it to treat personal data in accordance with the applicable provisions of this Privacy Policy and applicable law. In particular, you authorize us to subcontract the provision of technology and commercial services to members of our corporate group.

You represent and warrant that you have all the appropriate consents from data subjects whose personal data are submitted to us in the course of the provision of the Services. 

7. **International transfers**
                                                                                                                                                                                  The processing of your personal data will involve transferring it outside the European Economic Area (“**_EEA_**”). In addition, many of our external third parties are based outside the EEA, so their processing of your personal data will involve a transfer of data outside the EEA.

Whenever we transfer your personal data out of the EEA, we ensure a similar degree of protection is afforded to it by ensuring at least one of the following safeguards is implemented:

- We will only transfer your personal data to countries that have been deemed to provide an adequate level of protection for personal data by the European Commission. For further details, see European Commission: Adequacy of the protection of personal data in non-EU countries.
- Where we use certain service providers, we may use specific contracts approved by the European Commission which give personal data the same protection it has in Europe. For further details, see European Commission: Model contracts for the transfer of personal data to third countries.
- Where we use providers based in the US, we may transfer data to them if they provide protections pursuant to the European Commission’s standard contractual clauses.

Please contact us if you want further information on the specific mechanism used by us when transferring your personal data out of the EEA.

**8. Data security**
We have put in place appropriate security measures to prevent your personal data from being accidentally lost, used or accessed in an unauthorized way, altered or disclosed. In addition, we limit access to your personal data to those employees, agents, contractors and other third parties who have a business need to know. They will only process your personal data on our instructions, and they are subject to a duty of confidentiality.

We have put in place procedures to deal with any suspected personal data breach and will notify you and any applicable regulator of a breach where we are legally required to do so.

**9. Data retention**
We will only retain your personal data for as long as necessary to fulfill the purposes we collected it for, including for the purposes of satisfying any legal, accounting, or reporting requirements.

To determine the appropriate retention period for personal data, we consider the amount, nature, and sensitivity of the personal data, the potential risk of harm from unauthorized use or disclosure of your personal data, the purposes for which we process your personal data and whether we can achieve those purposes through other means, and the applicable legal requirements.

In some circumstances you can ask us to delete your data: see _Request erasure_ below for further in some circumstances we may anonymize your personal data (so that it can no longer be associated with you) for research or statistical purposes in which case we may use this information indefinitely without further notice to you.

10. **GDPR**
    If you are in the European Union or the EEA, you have the following rights in relation to your personal data:

- **Request access** to your personal data (commonly known as a “data subject access request”). This enables you to receive a copy of the personal data we hold about you and to check that we are lawfully processing it.
- **Request correction** of the personal data that we hold about you. This enables you to have any incomplete or inaccurate data we hold about you corrected, though we may need to verify the accuracy of the new data you provide to us.
- **Request erasure** of your personal data. This enables you to ask us to delete or remove personal data where there is no good reason for us continuing to process it. You also have the right to ask us to delete or remove your personal data where you have successfully exercised your right to object to processing (see below), where we may have processed your information unlawfully or where we are required to erase your personal data to comply with local law. Note, however, that we may not always be able to comply with your request of erasure for specific legal reasons which will be notified to you, if applicable, at the time of your request.
- **Object to processing** of your personal data where we are relying on a legitimate interest (or those of a third party) and there is something about your particular situation which makes you want to object to processing on this ground as you feel it impacts on your fundamental rights and freedoms. You also have the right to object where we are processing your personal data for direct marketing purposes. In some cases, we may demonstrate that we have compelling legitimate grounds to process your information which override your rights and freedoms.
- **Request restriction of processing** of your personal data. This enables you to ask us to suspend the processing of your personal data in the following scenarios: (a) if you want us to establish the data’s accuracy; (b) where our use of the data is unlawful but you do not want us to erase it; (c) where you need us to hold the data even if we no longer require it as you need it to establish, exercise or defend legal claims; or (d) you have objected to our use of your data but we need to verify whether we have overriding legitimate grounds to use it.
- **Request the transfer** of your personal data to you or to a third party. We will provide to you, or a third party you have chosen, your personal data in a structured, commonly used, machine-readable format. Note that this right only applies to automated information which you initially provided consent for us to use or where we used the information to perform a contract with you.
- **Withdraw consent at any time** where we are relying on consent to process your personal data. However, this will not affect the lawfulness of any processing carried out before you withdraw your consent. If you withdraw your consent, we may not be able to provide certain products or services to you. We will advise you if this is the case at the time you withdraw your consent.

If you wish to exercise any of the rights set out above, please contact us.

**A. No fee usually required**
You will not have to pay a fee to access your personal data (or to exercise any of the other rights). However, we may charge a reasonable fee if your request is clearly unfounded, repetitive or excessive. Alternatively, we may refuse to comply with your request in these circumstances.

**B. What we may need from you**
We may need to request specific information from you to help us confirm your identity and ensure your right to access your personal data (or to exercise any of your other rights). This is a security measure to ensure that personal data is not disclosed to any person who has no right to receive it. We may also contact you to ask you for further information in relation to your request to speed up our response.

**C. Time limit to respond**
We try to respond to all legitimate requests within one month. Occasionally it may take us longer than a month if your request is particularly complex or you have made a number of requests. In this case, we will notify you and keep you updated.

**11. Additional Privacy Rights**
The California Consumer Privacy Act or “**_CCPA_**” (Cal. Civ. Code § 1798.100 et. seq.) and similar statutes afford consumers residing in the applicable states certain rights with respect to their Personal Information (the state privacy statute that applies to you, if any, is referred to herein as the “**_State Privacy Law_**”). The rights below apply to all United States residents; provided, however, that your State Privacy Law will apply to the extent it affords you rights greater than those provided herein.

In the preceding 12 months, we have collected the following categories of Personal Information: identifiers, commercial information, internet or other electronic network activity information, and inferences. For details about the precise data points we collect and the categories of sources of such collection, please see the Collection of Information section above. We collect Personal Information for the business and commercial purposes described in the How Your Information is Used and Disclosure to Third Parties sections above. In the preceding 12 months, we have disclosed the following categories of Personal Information for business purposes to the following categories of recipients:

<table>
  <tr>
   <td><strong>Category of Personal Information</strong>
   </td>
   <td><strong>Categories of Recipients</strong>
   </td>
  </tr>
  <tr>
   <td>Identifiers
   </td>
   <td>Analytics Providers
<br />
Communication Providers
<br />
Customer Service Providers
<br />
Fraud Prevention and Security Providers
<br />
Infrastructure Providers
<br />
Marketing Providers
<br />
Payment Processors
   </td>
  </tr>
  <tr>
   <td>Commercial Information
   </td>
   <td>Analytics Providers
<br />
Infrastructure Providers
<br />
Payment Processors
   </td>
  </tr>
  <tr>
   <td>Internet or Other Electronic Network Activity Information
   </td>
   <td>Analytics Providers
<br />
Infrastructure Providers
   </td>
  </tr>
  <tr>
   <td>Inferences
   </td>
   <td>Analytics Providers
<br />
Infrastructure Providers
   </td>
  </tr>
</table>

Recipients we currently share Personal Information with include, but are not limited to, AWS (an infrastructure provider).

We do not sell your Personal Information.

Subject to certain limitations, you have the right to (1) request to know more about the categories and specific pieces of Personal Information we collect, use, and disclose about you, (2) request deletion of your Personal Information, (3) opt out of any sales of your Personal Information, if we engage in that activity in the future, and (4) not be discriminated against for exercising these rights. You may make these requests by emailing us at: support@logseq.com. We will verify a webform request by asking you to provide identifying information. We will not discriminate against you if you exercise your rights under a State Privacy Law.

If we receive your request from an authorized agent, we may ask for evidence that you have provided such agent with a power of attorney or that the agent otherwise has valid written authority to submit requests to exercise rights on your behalf. This may include requiring you to verify your identity. If you are an authorized agent seeking to make a request, please email us at: [email address].

We do not knowingly collect Personal Information from persons under the age of 18. Data will not be shared with any third parties for their own marketing purposes.

**12. Governing Law**
The terms of this Privacy Policy are governed by and in accordance with the laws of the state of New York, and, where applicable, the laws of the countries where our servers are located. You should not provide us with any Personal Information unless you consent to the application of United States law and, where applicable, New York law, and to the use and disclosure of your information in accordance with the terms of this Privacy Policy.

**13. Glossary**
**A. Lawful basis**
**Legitimate Interest** means the interest of our business in conducting and managing our business to enable us to give you the best service/product and the best and most secure experience. We make sure we consider and balance any potential impact on you (both positive and negative) and your rights before we process your personal data for our legitimate interests. We do not use your personal data for activities where our interests are overridden by the impact on you (unless we have your consent or are otherwise required or permitted to by law). You can obtain further information about how we assess our legitimate interests against any potential impact on you in respect of specific activities by contacting us.

**Performance of Contract** means processing your data where it is necessary for the performance of a contract to which you are a party or to take steps at your request before entering into such a contract.

**Comply with a legal or regulatory obligation** means processing your personal data where it is necessary for compliance with a legal or regulatory obligation that we are subject to.

**B. Third parties**
**External Third Parties**

- Service providers acting as processors who provide IT and system administration services including AWS S3, Dynamodb, Sentry, Engineers, and Posthog.
- Professional advisers including lawyers, bankers, auditors and insurers who provide consultancy, banking, legal, insurance and accounting services.
- Tax regulators and other authorities who require reporting of processing activities in certain circumstances.